import type { MessageKey } from '../i18n/messages';
import type { PublicConfig } from '../api/types';

/**
 * Правила экрана входа вокруг регистрации и сброса пароля (AUTH-2, AUTH-3, AUTH-5).
 *
 * <p>Ни React, ни состояния: экран входа рисует форму, а решения «показывать ли ссылку», «что
 * не так с введённым» и «есть ли в адресной строке токен сброса» проверяются тестом. Иначе
 * убедиться, что при закрытой регистрации кнопка не показывается, можно было бы только собрав
 * приложение с нужной настройкой сервера.
 */

/** Что сейчас показывает экран входа. */
export type AuthFormMode = 'login' | 'register' | 'verifySent' | 'forgot' | 'forgotSent' | 'reset';

/**
 * Показывать ли «Создать аккаунт».
 *
 * <p>Пока настройки не пришли (сервер недоступен, ответ ещё в пути) — не показываем: ссылка,
 * ведущая в 403, хуже отсутствующей. Неизвестный режим от сервера новее клиента тоже не
 * открывает форму.
 */
export function canSelfRegister(config: PublicConfig | null): boolean {
  return config?.registrationMode === 'OPEN';
}

/**
 * Объяснение, почему регистрация недоступна, — для режима INVITE. У закрытого режима ссылки
 * нет вовсе, и объяснять нечего.
 */
export function registrationClosedReason(config: PublicConfig | null): MessageKey | null {
  return config?.registrationMode === 'INVITE' ? 'registerInviteOnly' : null;
}

type FieldProblem = { field: 'login' | 'email' | 'password' | 'displayName'; message: MessageKey };

/** Совпадает с ограничениями сервера: 8 символов минимум, 128 максимум. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Проверка ровно на форму «что-то@что-то.что-то»: строже — значит отбивать существующие
 * адреса, а окончательный ответ всё равно за письмом, которое либо дойдёт, либо нет.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Набор символов логина — тот же, что у `@Pattern` в `LoginRequest` на сервере. */
const LOGIN_SHAPE = /^[a-zA-Z0-9._-]+$/;

/** Совпадает с `@Size(min = 2)` у поля логина на сервере. */
const MIN_LOGIN_LENGTH = 2;

/**
 * Проверка формы до отправки. Не заменяет серверную, а избавляет от похода в сеть ради
 * заведомого отказа — и, что важнее, называет проблемное поле: сервер на ошибку валидации
 * отвечает одним общим сообщением, по которому непонятно, что именно исправлять.
 */
export function validateRegistration(input: {
  email: string;
  password: string;
  displayName: string;
}): FieldProblem | null {
  const email = input.email.trim();
  if (!EMAIL_SHAPE.test(email)) {
    return { field: 'email', message: 'registerEmailInvalid' };
  }
  if (input.password.length < MIN_PASSWORD_LENGTH) {
    return { field: 'password', message: 'registerPasswordTooShort' };
  }
  if (input.displayName.trim().length === 0) {
    return { field: 'displayName', message: 'registerNameRequired' };
  }
  return null;
}

/**
 * Проверка формы входа до отправки (AUTH-13).
 *
 * <p><b>Зачем вообще.</b> Раньше её не было: экран отправлял на сервер что угодно, включая
 * пустые поля, а показывал в ответ сырой текст валидатора. Разбор логов прода 2026-09-13 нашёл
 * 22 отказа `400` на `/api/auth/login` подряд — `must not be blank`, `size must be between 2
 * and 64`, — и все они ушли в сеть из формы, которая могла ответить на них сама. Ревьюер Apple
 * при этом получал текст Hibernate Validator на китайском: сервер подбирает язык по своему
 * `Accept-Language`, а не по языку приложения.
 *
 * <p><b>Почему адрес почты законен.</b> Логин при регистрации собирается из части адреса до
 * собаки и человеку не показывается, так что адрес — единственное, что он о себе точно знает.
 * Сервер принимает оба варианта; здесь только отделяем одно от другого, чтобы подсказка была
 * по делу: у адреса спрашиваем форму адреса, у логина — набор символов.
 */
export function validateLoginForm(input: { login: string; password: string }): FieldProblem | null {
  const login = input.login.trim();
  if (login.length === 0) {
    return { field: 'login', message: 'loginRequired' };
  }
  if (login.includes('@')) {
    // Собака есть — значит человек вводил адрес, и говорить ему про набор символов логина
    // было бы ответом не на тот вопрос.
    if (!EMAIL_SHAPE.test(login)) {
      return { field: 'login', message: 'registerEmailInvalid' };
    }
  } else if (login.length < MIN_LOGIN_LENGTH || !LOGIN_SHAPE.test(login)) {
    return { field: 'login', message: 'loginInvalid' };
  }
  // Пароль не проверяем на длину: минимум в 8 символов — правило для НОВОГО пароля, а войти
  // может и тот, чей пароль заведён раньше этого правила или выдан администратором.
  if (input.password.length === 0) {
    return { field: 'password', message: 'passwordRequired' };
  }
  return null;
}

/**
 * Проверка адреса в форме «забыли пароль» (AUTH-13). Отдельная от `validateRegistration`, хотя
 * правило то же: там адрес — одно из трёх полей, здесь единственное, и возвращать «проблема в
 * поле email» из функции, которой передали только его, короче, чем собирать пустую регистрацию.
 *
 * <p>В логах прода за 11–12 сентября `/api/auth/forgot-password` ответил `400` семнадцать раз
 * из семнадцати — и каждый раз с `поле email: must not be blank`. То есть запасной путь «не
 * помню пароль» не работал вовсе: форма отправляла пустоту.
 */
export function validateForgotEmail(email: string): FieldProblem | null {
  return EMAIL_SHAPE.test(email.trim()) ? null : { field: 'email', message: 'registerEmailInvalid' };
}

export function validateNewPassword(password: string): FieldProblem | null {
  return password.length < MIN_PASSWORD_LENGTH
    ? { field: 'password', message: 'registerPasswordTooShort' }
    : null;
}

/**
 * Токен сброса из адреса страницы (веб). Ссылка из письма ведёт на `/reset-password?token=…`,
 * и по ней человек попадает на тот же экран входа — отдельного маршрута у веб-сборки нет.
 *
 * <p>Возвращает `null` на всём, что не похоже на нашу ссылку: чужой путь, пустой параметр,
 * мусор вместо адреса. Ошибку разбора глотаем — экран входа обязан открыться в любом случае.
 */
export function resetTokenFromUrl(href: string | null | undefined): string | null {
  if (!href) return null;
  try {
    const url = new URL(href);
    if (!url.pathname.startsWith('/reset-password')) return null;
    const token = url.searchParams.get('token');
    return token && token.trim().length > 0 ? token : null;
  } catch {
    return null;
  }
}

/**
 * Итог подтверждения адреса из адресной строки (веб).
 *
 * <p>Ссылка из письма ведёт на API, а тот перенаправляет сюда с `?verified=1` или
 * `?verified=0`: показывать человеку пустую страницу или JSON с ошибкой — значит обрывать
 * поток ровно на последнем шаге. Итог приходит параметром, а словами его объясняет экран.
 */
type VerifyOutcome = 'ok' | 'failed' | null;

export function verifyOutcomeFromUrl(href: string | null | undefined): VerifyOutcome {
  if (!href) return null;
  try {
    const value = new URL(href).searchParams.get('verified');
    if (value === '1') return 'ok';
    if (value === '0') return 'failed';
    return null;
  } catch {
    return null;
  }
}

/**
 * Нужен ли виджет CAPTCHA на текущей форме (AUTH-4 / стори 1.6).
 *
 * <p>На регистрации — всегда, когда CAPTCHA включена: это ручка, которая заводит строки в
 * базе и шлёт письма от нашего имени по адресу, выбранному снаружи.
 *
 * <p>На входе — только после того, как сервер ответил «нужна CAPTCHA». AC стори прямо
 * требует, чтобы обычный человек при первой попытке её не видел: показывать всем — это налог
 * на всех ради защиты от немногих, и сервер сам решает, когда порог пройден.
 */
export function needsCaptcha(
  mode: AuthFormMode,
  config: PublicConfig | null,
  serverAskedOnLogin: boolean,
): boolean {
  const siteKey = config?.captchaSiteKey ?? "";
  if (siteKey.length === 0) return false;
  if (mode === 'register') return true;
  return mode === 'login' && serverAskedOnLogin;
}

/**
 * Живая подсказка «8+ символов» под полем нового пароля (аудит 2026-10-04, п. 36).
 *
 * <p>Раньше правило было видно только после ошибки: человек придумывал пароль, жал «Создать»
 * и лишь тогда узнавал, что семи символов мало. Подсказка висит под полем с первой секунды и
 * зеленеет, когда правило выполнено, — то есть отвечает на вопрос до того, как его зададут.
 *
 * <p>`'empty'` отдельно от `'short'`: пустое поле — не ошибка, а ещё не начатый ввод, и
 * красить его красным значило бы ругать за то, чего человек ещё не делал.
 */
export type PasswordHintState = 'empty' | 'short' | 'ok';

export function passwordHintState(password: string): PasswordHintState {
  if (password.length === 0) return 'empty';
  return password.length >= MIN_PASSWORD_LENGTH ? 'ok' : 'short';
}

/** Поля формы регистрации, у которых своя строка ошибки под полем. */
export type RegistrationField = 'email' | 'password' | 'displayName';

/**
 * Проверка одного поля регистрации — на уходе фокуса (п. 36).
 *
 * <p>Проверять всю форму на уходе из первого поля нельзя: человек ещё не дошёл до пароля, а
 * под ним уже горело бы «слишком короткий». Поэтому по одному полю — тому, которое он только
 * что заполнил. Пустое поле на уходе не ругаем: его могли пропустить, чтобы вернуться.
 */
export function registrationFieldProblem(
  field: RegistrationField,
  input: { email: string; password: string; displayName: string },
): MessageKey | null {
  if (field === 'email') {
    const email = input.email.trim();
    return email.length > 0 && !EMAIL_SHAPE.test(email) ? 'registerEmailInvalid' : null;
  }
  if (field === 'password') {
    return passwordHintState(input.password) === 'short' ? 'registerPasswordTooShort' : null;
  }
  return null;
}

/**
 * Все проблемы формы регистрации сразу, по полям — для нажатия «Создать». Ошибка стоит под
 * своим полем, а не одной строкой внизу: из «Введите имя» под кнопкой непонятно, какое из
 * трёх полей виновато.
 */
export function registrationProblems(input: {
  email: string;
  password: string;
  displayName: string;
}): Partial<Record<RegistrationField, MessageKey>> {
  const out: Partial<Record<RegistrationField, MessageKey>> = {};
  if (!EMAIL_SHAPE.test(input.email.trim())) out.email = 'registerEmailInvalid';
  if (input.password.length < MIN_PASSWORD_LENGTH) out.password = 'registerPasswordTooShort';
  if (input.displayName.trim().length === 0) out.displayName = 'registerNameRequired';
  return out;
}

/**
 * Что делать после ответа на регистрацию.
 *
 * <p>Сервер с выключенным подтверждением почты отвечает `verificationRequired=false` — аккаунт
 * уже рабочий. Раньше экран всё равно показывал «проверьте почту», и человек ждал письма,
 * которое никто не отправлял. Теперь в этом случае сразу входим тем же адресом и паролем.
 */
export function registrationNextStep(
  response: { verificationRequired?: boolean } | null,
): 'verify' | 'signIn' {
  return response?.verificationRequired === false ? 'signIn' : 'verify';
}

/**
 * Почему не сработала ссылка сброса пароля (аудит 2026-10-04, п. 39).
 *
 * <p>Сервер отвечает по-английски («Reset link has expired») — раньше этот текст и
 * показывался, в любом языке интерфейса. Здесь он сводится к причине, а слова подбирает
 * экран. Тексты сервера стабильны: они заданы в `PasswordResetService`.
 *
 * <p>`null` — отказ не про ссылку (например, слишком короткий пароль): тогда работает обычный
 * разбор ошибки.
 */
export type ResetLinkProblem = 'expired' | 'used' | 'invalid';

export function resetLinkProblem(status: number, serverText: string): ResetLinkProblem | null {
  const text = serverText.toLowerCase();
  if (status === 410) return text.includes('used') ? 'used' : 'expired';
  if (status === 400 && /reset (link|token)/.test(text)) return 'invalid';
  return null;
}

/** Слова для причины отказа ссылки сброса. */
export function resetLinkProblemMessage(problem: ResetLinkProblem): MessageKey {
  if (problem === 'expired') return 'resetLinkExpired';
  if (problem === 'used') return 'resetLinkUsed';
  return 'resetLinkInvalid';
}

/**
 * Неверный текущий пароль при смене пароля или удалении аккаунта (п. 9).
 *
 * <p>Сервер отвечает 403 «Invalid password». Раньше это был 401, и клиент читал его как
 * протухшую сессию — выкидывал из аккаунта за опечатку. Теперь код другой, но и текст
 * «Invalid password» по-английски показывать нельзя: ошибка встаёт под поле пароля своими
 * словами. Проверяем и код, и текст: 403 бывает и по другим причинам, и называть их
 * «неверным паролем» было бы неправдой.
 */
export function isWrongCurrentPassword(status: number, serverText: string): boolean {
  return status === 403 && /invalid password/i.test(serverText);
}

/**
 * Отказ входа своими словами. Сервер пишет по-английски («Invalid credentials», «Email is not
 * verified», «Registration is closed») — эти фразы стабильны и заданы в `AuthService` и
 * `RegistrationService`. `null` — разбирать дальше обычным путём (`humanizeError`).
 */
export function authFailureMessage(status: number, serverText: string): MessageKey | null {
  const text = serverText.toLowerCase();
  if (status === 401 && text.includes('invalid credentials')) return 'loginWrongCredentials';
  if (status === 403 && text.includes('not verified')) return 'loginEmailNotVerified';
  if (status === 403 && text.includes('account deleted')) return 'loginAccountDeleted';
  if (status === 403 && text.includes('invitation')) return 'registerInviteOnly';
  if (status === 403 && text.includes('registration is closed')) return 'registerClosed';
  return null;
}
